import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { HashScrollHandler } from "./hooks/useGoToContact";
import Home from "./components/Home";
import CustomerDashboard from "./components/CustomerDashboard";
import AuthCallback from "./components/AuthCallback";
import AdminPanel from "./components/AdminPanel";
import AdminLogin from "./components/AdminLogin";
import ResetPassword from "./components/ResetPassword";
import HeroPreview from "./components/HeroPreview";
import Belzona from "./components/Belzona";
import JobsCareers from "./components/JobsCareers";
import Customers from "./components/Customers";
import EquipmentRepair from "./components/services/EquipmentRepair";
import ProtectiveCoatings from "./components/services/ProtectiveCoatings";
import PipelineRepair from "./components/services/PipelineRepair";
import StructuralRepair from "./components/services/StructuralRepair";
import HVACRepair from "./components/services/HVACRepair";
import AssetLifeExtension from "./components/services/AssetLifeExtension";
import IndustrialCleaning from "./components/services/IndustrialCleaning";
import TankRepair from "./components/services/TankRepair";
import GreaseInterceptors from "./components/services/GreaseInterceptors";
import RoofRepair from "./components/services/RoofRepair";
import FloorRepair from "./components/services/FloorRepair";
import EnginesCasings from "./components/services/EnginesCasings";
import FansBlowers from "./components/services/FansBlowers";
import GasketsSeals from "./components/services/GasketsSeals";
import SolidsHandling from "./components/services/SolidsHandling";
import MechanicalTransmission from "./components/services/MechanicalTransmission";
import MarineOffshore from "./components/services/MarineOffshore";
import SmartProgramOverview from "./components/maintenance/SmartProgramOverview";
import SystematicInspections from "./components/maintenance/SystematicInspections";
import MaterialSpecificSolutions from "./components/maintenance/MaterialSpecificSolutions";
import ActiveCorrosionControl from "./components/maintenance/ActiveCorrosionControl";
import ReliableRepairMethods from "./components/maintenance/ReliableRepairMethods";
import TotalAssetLifecycle from "./components/maintenance/TotalAssetLifecycle";
import CorrosionRust from "./components/research/CorrosionRust";
import LeaksSeepage from "./components/research/LeaksSeepage";
import ErosionWear from "./components/research/ErosionWear";
import EquipmentFailures from "./components/research/EquipmentFailures";
import HVACCooling from "./components/research/HVACCooling";
import StructuralDamage from "./components/research/StructuralDamage";
import OilAndGas from "./components/industries/OilAndGas";
import Steel from "./components/industries/Steel";
import Marine from "./components/industries/Marine";
import Power from "./components/industries/Power";
import PulpAndPaper from "./components/industries/PulpAndPaper";
import FacilitiesMaintenance from "./components/industries/FacilitiesMaintenance";
import WasteToEnergy from "./components/industries/WasteToEnergy";
import AgricultureFishing from "./components/industries/AgricultureFishing";
import WaterWastewater from "./components/industries/WaterWastewater";
import MiningQuarrying from "./components/industries/MiningQuarrying";
import GeneralIndustry from "./components/industries/GeneralIndustry";
import Manufacturing from "./components/industries/Manufacturing";
import Sugar from "./components/industries/Sugar";
import Education from "./components/industries/Education";
import FoodAndDrink from "./components/industries/FoodAndDrink";
import Petrochemical from "./components/industries/Petrochemical";
import Pharmaceutical from "./components/industries/Pharmaceutical";
import WindPower from "./components/industries/WindPower";
import Hydrogen from "./components/industries/Hydrogen";
import WhyChooseBCS from "./components/about/WhyChooseBCS";
import OurTeam from "./components/about/OurTeam";
import OurExperience from "./components/about/OurExperience";
import CustomerTestimonials from "./components/about/CustomerTestimonials";
import LegalDisclaimer from "./components/about/LegalDisclaimer";
import PrivacyPolicy from "./components/about/PrivacyPolicy";
import Associations from "./components/about/Associations";
import MissionStatement from "./components/about/MissionStatement";
import EditorTest from "./components/EditorTest";
import FieldReportBuilder from "./components/crm/FieldReportBuilder";
import CaseStudiesList from "./components/casestudies/CaseStudiesList";
import CreateCaseStudy from "./components/casestudies/CreateCaseStudy";
import CaseStudyDetail from "./components/casestudies/CaseStudyDetail";
import EditCaseStudy from "./components/casestudies/EditCaseStudy";
import MarketingSheet from "./components/MarketingSheet";
import BlogList from "./components/BlogList";
import BlogPost from "./components/BlogPost";
import { California, SouthernCalifornia, LosAngeles, OrangeCounty, SanDiego, Irvine, LongBeach, Anaheim } from "./components/service-areas/index";
import { Toaster } from "./components/ui/sonner";

// AppRouter component to handle auth callback routing
function AppRouter() {
  const location = useLocation();
  
  // Handle hash-based scrolling (e.g., /#contact)
  useEffect(() => {
    if (location.hash) {
      const elementId = location.hash.replace('#', '');
      // Small delay to ensure page is rendered
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // Scroll to top on route change without hash
      window.scrollTo(0, 0);
    }
  }, [location]);
  
  // Check URL fragment for session_id (OAuth callback)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
    <HashScrollHandler />
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/customer-portal" element={<CustomerDashboard />} />
      <Route path="/customer-portal/case-studies" element={<CaseStudiesList />} />
      <Route path="/customer-portal/case-studies/create" element={<CreateCaseStudy />} />
      <Route path="/customer-portal/case-studies/:id/edit" element={<EditCaseStudy />} />
      <Route path="/customer-portal/case-studies/:id" element={<CaseStudyDetail />} />
      <Route path="/admin" element={<AdminPanel />} />
      <Route path="/admin-login" element={<AdminLogin />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/hero-preview" element={<HeroPreview />} />
      <Route path="/marketing-sheet" element={<MarketingSheet />} />
      <Route path="/belzona" element={<Belzona />} />
      <Route path="/careers" element={<JobsCareers />} />
      <Route path="/customers" element={<Customers />} />
      
      {/* Services Routes */}
      <Route path="/services/equipment-repair" element={<EquipmentRepair />} />
      <Route path="/services/protective-coatings" element={<ProtectiveCoatings />} />
      <Route path="/services/pipeline-repair" element={<PipelineRepair />} />
      <Route path="/services/structural-repair" element={<StructuralRepair />} />
      <Route path="/services/hvac-repair" element={<HVACRepair />} />
      <Route path="/services/asset-life-extension" element={<AssetLifeExtension />} />
      <Route path="/services/industrial-cleaning" element={<IndustrialCleaning />} />
      <Route path="/services/tank-repair" element={<TankRepair />} />
      <Route path="/services/grease-interceptors" element={<GreaseInterceptors />} />
      <Route path="/services/roof-repair" element={<RoofRepair />} />
      <Route path="/services/floor-repair" element={<FloorRepair />} />
      <Route path="/services/engines-casings" element={<EnginesCasings />} />
      <Route path="/services/fans-blowers" element={<FansBlowers />} />
      <Route path="/services/gaskets-seals" element={<GasketsSeals />} />
      <Route path="/services/solids-handling" element={<SolidsHandling />} />
      <Route path="/services/mechanical-transmission" element={<MechanicalTransmission />} />
      <Route path="/services/marine-offshore" element={<MarineOffshore />} />
      
      {/* Industries Routes */}
      <Route path="/industries/oil-gas" element={<OilAndGas />} />
      <Route path="/industries/steel" element={<Steel />} />
      <Route path="/industries/marine" element={<Marine />} />
      <Route path="/industries/power" element={<Power />} />
      <Route path="/industries/pulp-paper" element={<PulpAndPaper />} />
      <Route path="/industries/facilities-maintenance" element={<FacilitiesMaintenance />} />
      <Route path="/industries/waste-to-energy" element={<WasteToEnergy />} />
      <Route path="/industries/agriculture-fishing" element={<AgricultureFishing />} />
      <Route path="/industries/water-wastewater" element={<WaterWastewater />} />
      <Route path="/industries/mining-quarrying" element={<MiningQuarrying />} />
      <Route path="/industries/general-industry" element={<GeneralIndustry />} />
      <Route path="/industries/manufacturing" element={<Manufacturing />} />
      <Route path="/industries/sugar" element={<Sugar />} />
      <Route path="/industries/education" element={<Education />} />
      <Route path="/industries/food-drink" element={<FoodAndDrink />} />
      <Route path="/industries/petrochemical" element={<Petrochemical />} />
      <Route path="/industries/pharmaceutical" element={<Pharmaceutical />} />
      <Route path="/industries/wind-power" element={<WindPower />} />
      <Route path="/industries/hydrogen" element={<Hydrogen />} />
      
      {/* Maintenance Routes */}
      <Route path="/maintenance/smart-program" element={<SmartProgramOverview />} />
      <Route path="/maintenance/systematic-inspections" element={<SystematicInspections />} />
      <Route path="/maintenance/material-specific-solutions" element={<MaterialSpecificSolutions />} />
      <Route path="/maintenance/active-corrosion-control" element={<ActiveCorrosionControl />} />
      <Route path="/maintenance/reliable-repair-methods" element={<ReliableRepairMethods />} />
      <Route path="/maintenance/total-asset-lifecycle" element={<TotalAssetLifecycle />} />
      
      {/* Research Routes */}
      <Route path="/research/corrosion-rust" element={<CorrosionRust />} />
      <Route path="/research/leaks-seepage" element={<LeaksSeepage />} />
      <Route path="/research/erosion-wear" element={<ErosionWear />} />
      <Route path="/research/equipment-failures" element={<EquipmentFailures />} />
      <Route path="/research/hvac-cooling" element={<HVACCooling />} />
      <Route path="/research/structural-damage" element={<StructuralDamage />} />
      
      {/* About Routes */}
      <Route path="/about/why-choose-bcs" element={<WhyChooseBCS />} />
      <Route path="/about/our-team" element={<OurTeam />} />
      <Route path="/about/our-experience" element={<OurExperience />} />
      <Route path="/about/testimonials" element={<CustomerTestimonials />} />
      <Route path="/about/associations" element={<Associations />} />
      <Route path="/about/mission-statement" element={<MissionStatement />} />
      <Route path="/dev/editor-test" element={<EditorTest />} />
      <Route path="/admin/field-report/new" element={<FieldReportBuilder />} />
      <Route path="/admin/field-report/edit/:id" element={<FieldReportBuilder />} />
      <Route path="/about/legal-disclaimer" element={<LegalDisclaimer />} />
      <Route path="/about/privacy-policy" element={<PrivacyPolicy />} />

      {/* Blog / Research & Learn Routes */}
      <Route path="/research-learn" element={<BlogList />} />
      <Route path="/research-learn/:slug" element={<BlogPost />} />

      {/* Service Area / SEO Location Pages */}
      <Route path="/service-areas/california" element={<California />} />
      <Route path="/service-areas/southern-california" element={<SouthernCalifornia />} />
      <Route path="/service-areas/los-angeles" element={<LosAngeles />} />
      <Route path="/service-areas/orange-county" element={<OrangeCounty />} />
      <Route path="/service-areas/san-diego" element={<SanDiego />} />
      <Route path="/service-areas/irvine" element={<Irvine />} />
      <Route path="/service-areas/long-beach" element={<LongBeach />} />
      <Route path="/service-areas/anaheim" element={<Anaheim />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster />
      </div>
    </HelmetProvider>
  );
}

export default App;
